'use client'

import { useTranslation } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import * as React from 'react'

import { Box, ButtonV1, cn } from '@podo-app/design-system-temp'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { containerClassName?: React.HTMLAttributes<HTMLDivElement>['className'] }
>(({ className, containerClassName, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className={cn('overflow-hidden scrollbar-hide [&::-webkit-scrollbar]:hidden', containerClassName)}
    >
      <div
        ref={ref}
        className={cn('flex', orientation === 'horizontal' ? '-ml-2.5' : '-mt-2.5 flex-col', className)}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel()

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(orientation === 'horizontal' ? 'pl-2.5' : 'pt-2.5', className)}
        {...props}
      />
    )
  },
)
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof ButtonV1>>(
  ({ className, variant = 'ghost', size = 'xSmall', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel()
    const t = useTranslation()

    return (
      <ButtonV1
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute  h-5 w-5 rounded-full',
          orientation === 'horizontal'
            ? '-left-[30px] top-1/2 -translate-y-1/2'
            : '-top-[30px] left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        eventParams={{ name: UI_IDS.btn.scrollPrev, location: 'carousel', action: 'navigate' }}
        {...props}
      >
        <ArrowLeft className="h-2.5 w-2.5" />
        <span className="sr-only">{t('common.accessibility.previousSlide')}</span>
      </ButtonV1>
    )
  },
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof ButtonV1>>(
  ({ className, variant = 'ghost', size = 'xSmall', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel()
    const t = useTranslation()

    return (
      <ButtonV1
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute h-5 w-5 rounded-full',
          orientation === 'horizontal'
            ? '-right-[30px] top-1/2 -translate-y-1/2'
            : '-bottom-[30px] left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        eventParams={{ name: UI_IDS.btn.scrollNext, location: 'carousel', action: 'navigate' }}
        {...props}
      >
        <ArrowRight className="h-2.5 w-2.5" />
        <span className="sr-only">{t('common.accessibility.nextSlide')}</span>
      </ButtonV1>
    )
  },
)
CarouselNext.displayName = 'CarouselNext'

const CarouselIndicator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    count: number
    current: number
    color?: 'white' | 'default'
    size?: 'small' | 'medium'
  }
>(({ className, count = 0, current = 0, color = 'default', size = 'medium', ...props }, ref) => {
  return (
    <div ref={ref} className={cn('flex gap-[2.5px]', className)} {...props}>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          className={cn(
            'rounded-full',
            size === 'small' ? 'w-1.5 h-1.5' : 'w-2 h-2',
            color === 'white'
              ? 'bg-white data-[selected=false]:opacity-50'
              : 'bg-gray-200 data-[selected=true]:bg-gray-900',
          )}
          data-selected={index === current - 1}
        />
      ))}
    </div>
  )
})
CarouselIndicator.displayName = 'CarouselIndicator'

export { Carousel, type CarouselApi, CarouselContent, CarouselIndicator, CarouselItem, CarouselNext, CarouselPrevious }
